import { apiService } from '../services/apiService';
import { API_PATHS } from './apiPaths';

const SERVICE_NAME = 'processPayment';
const SALT_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export type PaymentCredentials = {
  apiKey: string;
  secretKey: string;
};

export type PaymentCredentialSource = {
  merchantId?: number;
  apiKey?: string;
  merchantSecret?: string;
};

function generatePaymentSalt(length = 32): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => SALT_CHARS[b % SALT_CHARS.length]).join('');
}

async function hmacSha256Hex(key: string, message: string): Promise<string> {
  const enc = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(message));
  return Array.from(new Uint8Array(sig), (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function buildProcessPaymentHeaders(
  payload: Record<string, unknown>,
  flowType: string,
  credentials: PaymentCredentials,
  isCheckout = false
): Promise<Record<string, string>> {
  const { apiKey, secretKey } = credentials;
  const salt = generatePaymentSalt();
  const timestamp = String(Math.floor(Date.now() / 1000));
  const bodyStr = JSON.stringify(payload);
  const message = `${SERVICE_NAME}${salt}${apiKey}${timestamp}${bodyStr}`;
  const signature = await hmacSha256Hex(secretKey, message);

  return {
    apiKey,
    salt,
    timestamp,
    signature,
    'X-Flow-Type': flowType.trim().toUpperCase(),
    isCheckout: String(isCheckout),
  };
}

export async function resolvePaymentCredentials(
  source: PaymentCredentialSource
): Promise<PaymentCredentials | null> {
  const fromSource = {
    apiKey: source.apiKey?.trim() ?? '',
    secretKey: source.merchantSecret?.trim() ?? '',
  };
  if (fromSource.apiKey && fromSource.secretKey) return fromSource;

  if (source.merchantId) {
    try {
      const res: any = await apiService.get(`${API_PATHS.GET_MERCHANT}/${source.merchantId}`);
      const data = res?.data ?? res;
      const apiKey = String(data?.merchantKey ?? data?.apiKey ?? '').trim();
      const secretKey = String(data?.merchantSecret ?? data?.secretKey ?? '').trim();
      if (apiKey && secretKey) return { apiKey, secretKey };
    } catch {
      /* fall through to env fallback */
    }
  }

  const envApiKey = import.meta.env.VITE_PAYMENT_API_KEY?.trim();
  const envSecretKey = import.meta.env.VITE_PAYMENT_SECRET_KEY?.trim() ?? envApiKey;
  if (envApiKey && envSecretKey) return { apiKey: envApiKey, secretKey: envSecretKey };

  return null;
}
